/**
 * @module cache
 * @description Public cache adapter contract surface. Runtime cache
 * implementations live in provider packages; this subpath exports only the
 * interface, Zod schemas, and thin error class.
 */

import type {
  AdapterHealth as SharedAdapterHealth,
  AdapterJsonValue as SharedAdapterJsonValue,
  CacheAdapter as SharedCacheAdapter,
  CacheEntry as SharedCacheEntry,
  CacheSetOptions as SharedCacheSetOptions,
} from "@geenius/adapters";
import * as SharedAdapters from "@geenius/adapters";

/**
 * Health result returned by cache providers after checking backend
 * connectivity and basic read/write availability.
 *
 * @example
 * ```ts
 * const health: AdapterHealth = await cache.getHealth();
 * ```
 */
export type AdapterHealth = SharedAdapterHealth;

/**
 * SDK-free key/value cache adapter contract implemented by in-process,
 * Redis-like, KV-backed, and app-owned cache providers.
 *
 * @example
 * ```ts
 * await cache.set("feature", true, { ttlSeconds: 60 });
 * ```
 */
export type CacheAdapter = SharedCacheAdapter;

/**
 * Serializable cache record returned by cache conformance fixtures and
 * provider-boundary validators.
 *
 * @example
 * ```ts
 * const entry: CacheEntry = { key: "feature", value: true, updatedAt: new Date().toISOString() };
 * ```
 */
export type CacheEntry = SharedCacheEntry;

/**
 * Caller-controlled write options accepted by `CacheAdapter.set()`.
 *
 * @example
 * ```ts
 * const options: CacheSetOptions = { ttlSeconds: 60 };
 * ```
 */
export type CacheSetOptions = SharedCacheSetOptions;

/**
 * JSON-serializable value union accepted by cache writes; cache adapters
 * persist values across process and network boundaries so non-JSON values
 * (functions, classes, symbols) are rejected by `CacheAdapter.set()`.
 *
 * @example
 * ```ts
 * const value: AdapterJsonValue = { enabled: true };
 * ```
 */
export type AdapterJsonValue = SharedAdapterJsonValue;

/**
 * Runtime validator for the common cache adapter health response.
 *
 * @example
 * ```ts
 * const health = AdapterHealthSchema.parse({ ok: true, checkedAt: new Date().toISOString() });
 * ```
 */
export const AdapterHealthSchema = SharedAdapters.AdapterHealthSchema;

/**
 * Runtime validator for JSON-serializable cache values; rejects functions,
 * symbols, class instances, and other non-JSON payloads at the contract
 * boundary before the cache backend receives them.
 *
 * @example
 * ```ts
 * const value = AdapterJsonValueSchema.parse({ enabled: true });
 * ```
 */
export const AdapterJsonValueSchema = SharedAdapters.AdapterJsonValueSchema;

/**
 * Contract-layer error for invalid cache payloads or provider-boundary
 * violations before a concrete cache SDK receives the request.
 *
 * @example
 * ```ts
 * throw new CacheContractError("Cache value is not JSON-compatible");
 * ```
 */
export const CacheContractError = SharedAdapters.CacheContractError;

/**
 * Runtime validator for serializable cache records.
 *
 * @example
 * ```ts
 * const entry = CacheEntrySchema.parse({ key: "feature", value: true, updatedAt: new Date().toISOString() });
 * ```
 */
export const CacheEntrySchema = SharedAdapters.CacheEntrySchema;

/**
 * Runtime validator for cache write options such as TTL.
 *
 * @example
 * ```ts
 * const options = CacheSetOptionsSchema.parse({ ttlSeconds: 60 });
 * ```
 */
export const CacheSetOptionsSchema = SharedAdapters.CacheSetOptionsSchema;
